import { Link, useParams } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Gallery from "@/pages/works/components/Gallery";
import { getRelatedWorks, getWorkById } from "@/mocks/works";

export default function WorksDetail() {
  const { id = "" } = useParams();
  const work = getWorkById(id);

  usePageMeta({
    title: work
      ? `${work.title} - 张津瑜品牌作品集`
      : "作品未找到 - 张津瑜品牌作品集",
    description: work ? work.summary.slice(0, 150) : "抱歉，您查看的作品不存在。",
    keywords: work ? ["张津瑜作品", work.type, work.title].join(",") : "张津瑜作品",
  });

  if (!work) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-3xl flex-col items-center justify-center px-4 py-20 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-gallery-line text-3xl" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
          抱歉，没有找到这件作品
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          该作品可能已被移除或链接有误，欢迎返回作品展示页浏览其他内容。
        </p>
        <Link
          to="/works"
          className="mt-6 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回作品展示
        </Link>
      </div>
    );
  }

  const related = getRelatedWorks(work, 4);
  const images = [work.cover, ...work.gallery];

  return (
    <>
      <section className="border-b border-background-200 bg-background-100 py-6">
        <div className="mx-auto max-w-6xl px-4 md:px-6 lg:px-10">
          <Breadcrumb
            items={[
              { label: "作品展示", to: "/works" },
              { label: work.type, to: `/works?type=${encodeURIComponent(work.type)}` },
              { label: "详情" },
            ]}
          />
        </div>
      </section>

      <section className="bg-background-50 py-10 md:py-14">
        <div className="mx-auto max-w-6xl px-4 md:px-6 lg:px-10">
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-primary-50 px-3 py-1 font-label text-xs font-medium text-primary-700">
              {work.type}
            </span>
            <span className="rounded-full bg-accent-100 px-3 py-1 font-label text-xs font-medium text-accent-900">
              {work.year}
            </span>
            <span className="flex items-center gap-1.5 text-xs text-foreground-500">
              <i className="ri-calendar-line" />
              {work.date}
            </span>
            <span className="flex items-center gap-1.5 text-xs text-foreground-500">
              <i className="ri-eye-line" />
              {work.views.toLocaleString()}
            </span>
          </div>

          <h1 className="mt-5 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
            {work.title}
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {work.summary}
          </p>

          <div className="mt-8">
            <Gallery images={images} title={work.title} />
          </div>

          <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_280px]">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950">
                项目背景介绍
              </h2>
              <div className="mt-5 space-y-5">
                {work.background.map((paragraph, index) => (
                  <p
                    key={index}
                    className="text-base leading-[1.9] text-foreground-800"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>

            <aside className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="font-heading text-sm font-bold text-foreground-950">
                作品信息
              </h3>
              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex items-center justify-between gap-4">
                  <dt className="text-foreground-500">作品名称</dt>
                  <dd className="text-right text-foreground-900">{work.title}</dd>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <dt className="text-foreground-500">作品类型</dt>
                  <dd className="text-foreground-900">{work.type}</dd>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <dt className="text-foreground-500">创作年份</dt>
                  <dd className="text-foreground-900">{work.year}</dd>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <dt className="text-foreground-500">发布时间</dt>
                  <dd className="text-foreground-900">{work.date}</dd>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <dt className="text-foreground-500">图片数量</dt>
                  <dd className="text-foreground-900">{images.length} 张</dd>
                </div>
              </dl>
              <Link
                to="/contact"
                className="mt-5 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-mail-send-line" />
                咨询合作
              </Link>
            </aside>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-4 md:px-6 lg:px-10">
          <div className="mb-8 flex items-center justify-between">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              相关作品推荐
            </h2>
            <Link
              to="/works"
              className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-accent-600"
            >
              全部作品
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((rel) => (
              <Link
                key={rel.id}
                to={`/works/detail/${rel.id}`}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
              >
                <div className="h-40 w-full overflow-hidden">
                  <img
                    src={rel.cover}
                    alt={`${rel.title} - 张津瑜相关作品`}
                    title={`${rel.title} + 张津瑜作品`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <span className="font-label text-xs font-semibold text-accent-600">
                    {rel.type}
                  </span>
                  <h3 className="mt-2 line-clamp-2 font-heading text-sm font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
                    {rel.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}